import { Controller } from "@hotwired/stimulus"

/**
 * Central state controller for an attribution provider panel.
 *
 * Manages:
 * - Provider identity
 * - Selected ad account
 * - Attribution mode (conversion/click time). One basis per (account, provider): the server renders
 *   this provider's basis (its attribution_v2_<account id>_<provider> cookie) into attributionValue,
 *   and only this panel's toggle changes it. Unlike the reports sidebar, which is one choice per viewer.
 * - Frame reloading
 *
 * Child controllers dispatch events that bubble up to this controller:
 * - attribution:account-changed - when account dropdown changes
 * - attribution:settings-saved - when conversion settings are saved
 * - attribution:refresh - generic refresh request
 */
export default class extends Controller {
  static targets = ["frame", "dropdown", "toggle", "convLabel", "clickLabel"]

  static values = {
    provider: String,
    selectedAccountId: Number,
    attribution: { type: String, default: "conversion" },
    accountId: Number,
    dashboardMode: { type: Boolean, default: false },
    baseUrl: String
  }

  connect() {
    // Initialize selected account from dropdown if present
    if (this.hasDropdownTarget) {
      this.selectedAccountIdValue = parseInt(this.dropdownTarget.value, 10)
    }

    this.updateToggleStyles()

    // Listen for settings-saved events at document level (drawer is outside panel DOM)
    this.handleSettingsSaved = this.handleSettingsSaved.bind(this)
    document.addEventListener("attribution:settings-saved", this.handleSettingsSaved)
  }

  disconnect() {
    document.removeEventListener("attribution:settings-saved", this.handleSettingsSaved)
  }

  /**
   * Handle settings saved event from document level.
   * Only refresh if the event is for this panel's provider.
   */
  handleSettingsSaved(event) {
    const { provider } = event.detail || {}
    if (provider === this.providerValue) {
      this.refresh()
    }
  }

  // ============================================================================
  // Event Handlers (called by child controllers via dispatch)
  // ============================================================================

  /**
   * Handle account selection change (from dropdown or drawer)
   * Expected detail: { accountAdAccountId, adAccountId }
   */
  accountChanged(event) {
    const { accountAdAccountId, adAccountId } = event.detail || {}

    if (accountAdAccountId) {
      this.selectedAccountIdValue = parseInt(accountAdAccountId, 10)

      // Sync dropdown if it exists and value differs
      if (this.hasDropdownTarget && this.dropdownTarget.value !== String(accountAdAccountId)) {
        this.dropdownTarget.value = accountAdAccountId
      }
    }

    this.refresh()

    // Dispatch event for drawer to sync its dropdown
    this.dispatch("sync", {
      detail: {
        provider: this.providerValue,
        accountAdAccountId: this.selectedAccountIdValue,
        adAccountId
      }
    })
  }

  /**
   * Handle settings saved (from drawer form)
   * Just refresh the panel to reflect new settings
   */
  settingsSaved(event) {
    this.refresh()
  }

  /**
   * Generic refresh request
   */
  refreshRequested(event) {
    this.refresh()
  }

  // ============================================================================
  // Actions (called directly from HTML)
  // ============================================================================

  /**
   * Handle dropdown change
   */
  selectAccount(event) {
    const selectedOption = this.dropdownTarget.options[this.dropdownTarget.selectedIndex]
    this.selectedAccountIdValue = parseInt(this.dropdownTarget.value, 10)

    this.refresh()

    // Dispatch sync event for drawer
    this.dispatch("sync", {
      detail: {
        provider: this.providerValue,
        accountAdAccountId: this.selectedAccountIdValue,
        adAccountId: selectedOption?.dataset.adAccountId
      }
    })
  }

  /**
   * Toggle attribution mode between conversion and click time
   */
  toggleAttribution(event) {
    event.preventDefault()

    if (this.hasToggleTarget) {
      this.toggleTarget.checked = !this.toggleTarget.checked
    }

    this.attributionValue = this.attributionValue === "click" ? "conversion" : "click"
    this.updateToggleStyles()
    // This request's ?attribution= is what the server saves as this provider's basis.
    this.refresh()
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  refresh() {
    if (!this.hasFrameTarget) return

    const frame = this.frameTarget
    let url

    // In dashboard mode, we need to fetch from the dashboard_v2 results endpoint
    if (this.dashboardModeValue && this.accountIdValue) {
      url = new URL(`/admin/dashboard_v2/${this.accountIdValue}/results`, window.location.origin)
    } else if (this.baseUrlValue) {
      url = new URL(this.baseUrlValue, window.location.origin)
    } else {
      url = new URL(window.location)
    }

    // Set required params for turbo frame request
    url.searchParams.set("provider", this.providerValue)

    if (this.selectedAccountIdValue) {
      url.searchParams.set("selected_account_id", this.selectedAccountIdValue)
    }

    url.searchParams.set("attribution", this.attributionValue)

    // Visual feedback during load
    frame.style.opacity = "0.3"
    frame.style.filter = "blur(2px)"

    frame.addEventListener("turbo:frame-load", () => {
      frame.style.opacity = "1"
      frame.style.filter = "none"
    }, { once: true })

    frame.src = url.toString()
  }

  updateToggleStyles() {
    const isClick = this.attributionValue === "click"

    if (this.hasConvLabelTarget) {
      this.convLabelTarget.classList.toggle("text-gray-900", !isClick)
      this.convLabelTarget.classList.toggle("font-medium", !isClick)
      this.convLabelTarget.classList.toggle("text-gray-500", isClick)
    }

    if (this.hasClickLabelTarget) {
      this.clickLabelTarget.classList.toggle("text-gray-900", isClick)
      this.clickLabelTarget.classList.toggle("font-medium", isClick)
      this.clickLabelTarget.classList.toggle("text-gray-500", !isClick)
    }

    if (this.hasToggleTarget) {
      this.toggleTarget.checked = isClick
    }
  }
}
